import { useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, ImagePlus } from 'lucide-react';
import toast from 'react-hot-toast';

interface ImageUploaderProps {
  images: File[];
  onImagesChange: (images: File[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
  accept?: Record<string, string[]>;
  className?: string;
  label?: string;
}

export function ImageUploader({
  images,
  onImagesChange,
  maxFiles = 5,
  maxSizeMB = 10,
  accept = { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
  className = '',
  label = 'Drop photos here or tap to upload',
}: ImageUploaderProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[], rejections: FileRejection[]) => {
      if (rejections.length > 0) {
        const error = rejections[0].errors[0];
        if (error.code === 'file-too-large') {
          toast.error(`File too large. Max ${maxSizeMB}MB.`);
        } else if (error.code === 'too-many-files') {
          toast.error(`Max ${maxFiles} files.`);
        } else {
          toast.error('Invalid file type');
        }
        return;
      }

      const total = images.length + acceptedFiles.length;
      if (total > maxFiles) {
        toast.error(`Max ${maxFiles} files allowed`);
        return;
      }

      onImagesChange([...images, ...acceptedFiles]);
    },
    [images, onImagesChange, maxFiles, maxSizeMB],
  );

  const removeImage = useCallback(
    (index: number) => {
      onImagesChange(images.filter((_, i) => i !== index));
    },
    [images, onImagesChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: maxFiles - images.length,
    maxSize: maxSizeMB * 1024 * 1024,
    disabled: images.length >= maxFiles,
  });

  return (
    <div className={className}>
      {/* Drop zone */}
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-2xl p-8 text-center
                    cursor-pointer transition-all ${
                      isDragActive
                        ? 'border-vestir-purple bg-vestir-purple/5'
                        : images.length >= maxFiles
                          ? 'border-vestir-gray bg-vestir-gray/20 cursor-not-allowed'
                          : 'border-vestir-gray hover:border-vestir-purple/50 hover:bg-vestir-purple/5'
                    }`}
        role="button"
        aria-label={label}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center gap-2">
          {isDragActive ? (
            <Upload size={32} className="text-vestir-purple animate-bounce" />
          ) : (
            <ImagePlus size={32} className="text-vestir-gray-dark" />
          )}

          <p className="text-sm text-vestir-gray-dark">
            {isDragActive
              ? 'Drop your photos here'
              : images.length >= maxFiles
                ? 'Maximum photos reached'
                : label}
          </p>

          <p className="text-xs text-vestir-gray-dark/70">
            JPG, PNG, WebP up to {maxSizeMB}MB
          </p>
        </div>
      </div>

      {/* Thumbnails */}
      <AnimatePresence>
        {images.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="flex gap-3 mt-4 overflow-x-auto scrollbar-none py-1"
          >
            {images.map((file, index) => (
              <motion.div
                key={`${file.name}-${index}`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden
                           ring-2 ring-vestir-gray"
              >
                <img
                  src={URL.createObjectURL(file)}
                  alt={`Upload ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeImage(index);
                  }}
                  className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full
                             flex items-center justify-center"
                  aria-label={`Remove image ${index + 1}`}
                >
                  <X size={12} className="text-white" />
                </button>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <p className="text-xs text-vestir-gray-dark mt-2">
        {images.length}/{maxFiles} photos
      </p>
    </div>
  );
}
